import React, { useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import {
  Upload, FolderCog, FileText, MinusCircle, Download, AlertTriangle, RotateCcw,
  Mail, MessageSquare, MessageCircle, Inbox, FileUp, CheckCircle2,
} from "lucide-react";
import DvsPaperAllocationService, {
  DvsAllocationDetail,
  DvsInstructorDetail,
  DvsUploadedPaper,
} from "../../_services/DvsPaperAllocationService";
import "./dvs-paper-allocation.css";

interface Props {
  testId: number;
  /** Show the "Upload Scanned Test Paper" button (upload-role authorised). */
  canUpload?: boolean;
  /** Show "Manage Digital Paper" + "Uploaded Scanned Paper" (manage-role authorised). */
  canManage?: boolean;
}

const ZIP_SAMPLE_URL =
  "https://tuningfork-live.s3-ap-southeast-1.amazonaws.com/nttf2/uploads/testPapers/BTC.zip";

const DvsPaperAllocationPanel: React.FC<Props> = ({ testId, canUpload = true, canManage = true }) => {
  // ---- Upload Scanned Test Paper ----
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string>("");
  const [uploadErrors, setUploadErrors] = useState<Array<{ fileName: string; message: string }>>([]);
  const [uploadErrorMsg, setUploadErrorMsg] = useState<string>("");

  // ---- Manage Digital Paper ----
  const [showManage, setShowManage] = useState(false);
  const [detail, setDetail] = useState<DvsAllocationDetail | null>(null);
  const [manageLoading, setManageLoading] = useState(false);
  const [assignInputs, setAssignInputs] = useState<Record<number, number>>({});
  const [assigning, setAssigning] = useState(false);

  // ---- Reclaim ----
  const [reclaimRow, setReclaimRow] = useState<DvsInstructorDetail | null>(null);
  const [reclaimCount, setReclaimCount] = useState<number>(0);
  const [reclaiming, setReclaiming] = useState(false);
  const [reclaimConfirm, setReclaimConfirm] = useState<number>(0); // # of inProgress papers needing confirm

  // ---- Uploaded Scanned Paper ----
  const [showUploaded, setShowUploaded] = useState(false);
  const [uploadedPapers, setUploadedPapers] = useState<DvsUploadedPaper[]>([]);
  const [uploadedLoading, setUploadedLoading] = useState(false);

  // ---- Send Alert (Not Submitted / Not Evaluated) ----
  const [alertType, setAlertType] = useState<null | "notSubmitted" | "notEvaluated">(null);
  const [alertChannels, setAlertChannels] = useState({ sms: false, email: false, whatsApp: false });
  const [alertSending, setAlertSending] = useState(false);

  // ---- Re-open evaluated paper ----
  const [reopenPaper, setReopenPaper] = useState<DvsUploadedPaper | null>(null);
  const [reopening, setReopening] = useState(false);

  // Derived allocation numbers
  const totalPapers = detail?.totalStudents?.[0] ?? 0;
  const unassigned = detail?.remainingStudentCount?.[0] ?? 0;
  const assignedPapers = totalPapers - unassigned;
  const readyToAssign = Object.values(assignInputs).reduce((s, n) => s + (Number(n) || 0), 0);
  const yetToAssign = unassigned - readyToAssign;

  // Set an instructor's assign count, clamped so the running total can never
  // exceed the number of unassigned papers available.
  const setAssignInput = (instructorId: number, raw: number) => {
    const val = Math.max(0, Number(raw) || 0);
    setAssignInputs((prev) => {
      const others = Object.entries(prev).reduce(
        (s, [k, v]) => (Number(k) === instructorId ? s : s + (Number(v) || 0)),
        0,
      );
      const maxForThis = Math.max(0, unassigned - others);
      return { ...prev, [instructorId]: Math.min(val, maxForThis) };
    });
  };

  // =================== Upload ===================
  const openUpload = () => {
    setUploadFile(null);
    setUploadSuccess("");
    setUploadErrors([]);
    setUploadErrorMsg("");
    setShowUpload(true);
  };

  const submitUpload = async () => {
    if (!uploadFile) {
      setUploadErrorMsg("*Please upload a zip file to process.");
      return;
    }
    if (!/\.zip$/i.test(uploadFile.name)) {
      setUploadErrorMsg("*File type mismatch. Only .zip file is supported.");
      return;
    }
    setUploading(true);
    setUploadSuccess("");
    setUploadErrors([]);
    setUploadErrorMsg("");
    try {
      const res = await DvsPaperAllocationService.uploadZip(testId, uploadFile, 0);
      if (res.status === 1) {
        setUploadSuccess(res.data?.message || "Upload successful.");
      } else if (res.data?.errors) {
        setUploadErrors(res.data.errors);
      } else {
        setUploadErrorMsg(typeof res.data === "string" ? res.data : "Upload failed.");
      }
    } catch (e: any) {
      setUploadErrorMsg(e?.response?.data?.error || e?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  // =================== Manage ===================
  const openManage = async () => {
    setShowManage(true);
    setManageLoading(true);
    setAssignInputs({});
    try {
      const data = await DvsPaperAllocationService.getDetail(testId);
      setDetail(data);
      if (data.autoAssignmentPaper) {
        // Clamp the auto-fill so the suggested totals never exceed the number of
        // unassigned papers (otherwise "Yet to be assigned" can go negative and
        // the Assign action fails server-side).
        let budget = data.remainingStudentCount?.[0] ?? 0;
        const prefill: Record<number, number> = {};
        data.details.forEach((d) => {
          const v = Math.min(Math.max(0, d.autoFillAssign || 0), budget);
          prefill[d.instructorId] = v;
          budget -= v;
        });
        setAssignInputs(prefill);
      }
    } catch {
      setDetail(null);
    } finally {
      setManageLoading(false);
    }
  };

  const submitAssign = async () => {
    if (!detail) return;
    const instructorData = detail.details
      .map((d) => ({
        instructorId: d.instructorId,
        instructorName: d.instructorName,
        papersToAssign: Number(assignInputs[d.instructorId] || 0),
      }))
      .filter((d) => d.papersToAssign > 0);

    if (instructorData.length === 0) {
      window.alert("No data to assign.");
      return;
    }
    if (readyToAssign > unassigned) {
      window.alert("Total assign paper shouldn't be greater than available paper.");
      return;
    }
    setAssigning(true);
    try {
      const res = await DvsPaperAllocationService.assign(testId, instructorData);
      if (res.status === 1) {
        await openManage();
      } else {
        window.alert(res.data || "Problem in test paper assign.");
      }
    } catch (e: any) {
      window.alert(e?.message || "Problem in test paper assign.");
    } finally {
      setAssigning(false);
    }
  };

  // =================== Reclaim ===================
  const openReclaim = (row: DvsInstructorDetail) => {
    setReclaimRow(row);
    setReclaimCount(0);
    setReclaimConfirm(0);
  };

  const doReclaim = async (includeInProgress: boolean) => {
    if (!reclaimRow) return;
    const totalReclaimable = reclaimRow.pending + reclaimRow.inProgress;
    if (reclaimCount <= 0) {
      window.alert("Please input positive number.");
      return;
    }
    if (reclaimCount > totalReclaimable) {
      window.alert(
        `Number of reclaim papers cannot be greater than total reclaimable papers (Pending: ${reclaimRow.pending} + InProgress: ${reclaimRow.inProgress} = ${totalReclaimable}).`,
      );
      return;
    }
    if (reclaimCount > reclaimRow.pending && !includeInProgress) {
      setReclaimConfirm(reclaimCount - reclaimRow.pending);
      return;
    }
    setReclaiming(true);
    try {
      const res = await DvsPaperAllocationService.reclaim({
        testId,
        instructorId: reclaimRow.instructorId,
        reclaimPapers: reclaimCount,
        includeInProgress: reclaimCount > reclaimRow.pending,
      });
      if (res.status === 1) {
        setReclaimRow(null);
        setReclaimConfirm(0);
        await openManage();
      } else {
        window.alert(res.data ? res.data.replace(/<br\/?>/g, "\n") : "Problem in test paper reclaim.");
      }
    } catch (e: any) {
      window.alert(e?.message || "Problem in test paper reclaim.");
    } finally {
      setReclaiming(false);
    }
  };

  // =================== Uploaded list ===================
  const openUploaded = async () => {
    setShowUploaded(true);
    setUploadedLoading(true);
    try {
      const list = await DvsPaperAllocationService.getUploadedPapers(testId);
      setUploadedPapers(list);
    } catch {
      setUploadedPapers([]);
    } finally {
      setUploadedLoading(false);
    }
  };

  // =================== Send Alert ===================
  const openAlert = (type: "notSubmitted" | "notEvaluated") => {
    setAlertChannels({ sms: false, email: false, whatsApp: false });
    setAlertType(type);
  };

  const submitAlert = async () => {
    if (!alertType) return;
    if (!alertChannels.sms && !alertChannels.email && !alertChannels.whatsApp) {
      window.alert("Please select at least one option.");
      return;
    }
    setAlertSending(true);
    try {
      const params = {
        testId,
        sendSms: alertChannels.sms,
        sendEmail: alertChannels.email,
        sendWhatsApp: alertChannels.whatsApp,
      };
      const res =
        alertType === "notSubmitted"
          ? await DvsPaperAllocationService.alertNotSubmitted(params)
          : await DvsPaperAllocationService.alertNotEvaluated(params);
      window.alert(res.message || "Done.");
      setAlertType(null);
    } catch (e: any) {
      window.alert(e?.message || "Failed to send alert.");
    } finally {
      setAlertSending(false);
    }
  };

  // =================== Re-open ===================
  const submitReopen = async () => {
    if (!reopenPaper) return;
    setReopening(true);
    try {
      const res = await DvsPaperAllocationService.reopen({
        id: reopenPaper.id,
        studentcode: reopenPaper.studentCode,
        studentname: reopenPaper.studentName,
      });
      window.alert(res.message || "Done.");
      setReopenPaper(null);
      await openUploaded();
    } catch (e: any) {
      window.alert(e?.message || "Failed to re-open evaluation.");
    } finally {
      setReopening(false);
    }
  };

  const toggleChannel = (key: "sms" | "email" | "whatsApp") =>
    setAlertChannels((p) => ({ ...p, [key]: !p[key] }));

  return (
    <>
      {/* ============ Trigger card ============ */}
      <div className="dvsa-panel card mb-3">
        <div className="dvsa-panel-head">
          <span className="dvsa-panel-icon"><FolderCog size={18} /></span>
          <div>
            <p className="dvsa-panel-title">Digital Evaluation Allocation</p>
            <p className="dvsa-panel-sub">Upload, assign and track scanned answer scripts</p>
          </div>
        </div>
        <div className="dvsa-panel-body">
          {canUpload && (
            <button className="dvsa-action-btn dvsa-btn-upload" onClick={openUpload}>
              <Upload size={16} />Upload Scanned Test Paper
            </button>
          )}
          {canManage && (
            <button className="dvsa-action-btn dvsa-btn-manage" onClick={openManage}>
              <FolderCog size={16} />Manage Digital Paper
            </button>
          )}
          {canManage && (
            <button className="dvsa-action-btn dvsa-btn-list" onClick={openUploaded}>
              <FileText size={16} />Uploaded Scanned Paper
            </button>
          )}
        </div>
      </div>

      {/* ============ Upload Scanned Test Paper Modal ============ */}
      <Modal className="dvsa-modal" show={showUpload} onHide={() => setShowUpload(false)} size="lg" centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Scanned Paper Upload</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uploadSuccess ? (
            <div className="dvsa-banner dvsa-banner-success">
              <CheckCircle2 size={16} style={{ marginRight: 8, verticalAlign: "-3px" }} />{uploadSuccess}
            </div>
          ) : null}

          <label htmlFor="dvsa-file" className="dvsa-drop" style={{ display: "block", cursor: "pointer" }}>
            <div className="dvsa-drop-icon"><FileUp size={26} /></div>
            <div className="dvsa-drop-title">Click to choose a .zip file</div>
            <div className="dvsa-drop-sub">A zip of <code>&lt;StudentCode&gt;.pdf</code> scanned answer scripts</div>
            {uploadFile ? <div className="dvsa-file-name"><FileText size={14} style={{ verticalAlign: "-2px" }} /> {uploadFile.name}</div> : null}
            <input
              id="dvsa-file"
              type="file"
              accept=".zip"
              style={{ display: "none" }}
              onChange={(e: any) => setUploadFile(e.target.files?.[0] || null)}
            />
          </label>

          <div className="dvsa-instructions">
            <strong>Instructions</strong>
            <ol>
              <li>Place each scanned PDF in a folder, naming every file as <code>&lt;StudentCode&gt;.pdf</code>.</li>
              <li>Zip the folder and upload it here.</li>
            </ol>
            <a className="dvsa-sample-link" href={ZIP_SAMPLE_URL} target="_blank" rel="noreferrer">
              <Download size={14} /> Download sample zip
            </a>
          </div>

          {uploadErrorMsg ? <div className="dvsa-banner dvsa-banner-danger" style={{ marginTop: 14, marginBottom: 0 }}>{uploadErrorMsg}</div> : null}

          {uploadErrors.length > 0 ? (
            <div style={{ marginTop: 16 }}>
              <div className="dvsa-banner dvsa-banner-danger">Please correct the errors below and retry.</div>
              <div className="dvsa-table-wrap dvsa-card" style={{ marginBottom: 0 }}>
                <table className="dvsa-table">
                  <thead><tr><th>File Name</th><th>Message</th></tr></thead>
                  <tbody>
                    {uploadErrors.map((err, i) => (
                      <tr key={i}>
                        <td>{err.fileName}</td>
                        <td style={{ color: "var(--status-danger)" }}>{err.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button className="dvsa-btn dvsa-btn-ghost" onClick={() => setShowUpload(false)}>Close</button>
          <button className="dvsa-btn dvsa-btn-primary" onClick={submitUpload} disabled={uploading}>
            {uploading ? <><Spinner animation="border" size="sm" className="me-1" />Uploading…</> : "Upload"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ============ Manage Digital Paper Modal ============ */}
      <Modal className="dvsa-modal" dialogClassName="dvs-wide-dialog" show={showManage} onHide={() => setShowManage(false)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Digital Test Paper · Evaluation Allocation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {manageLoading ? (
            <div className="dvsa-empty"><Spinner animation="border" /></div>
          ) : !detail ? (
            <div className="dvsa-empty">
              <div className="dvsa-empty-icon"><AlertTriangle size={26} /></div>
              Failed to load allocation details.
            </div>
          ) : (
            <div className="row g-3">
              <div className="col-md-3">
                <div className="dvsa-card">
                  <div className="dvsa-card-head"><FileText size={16} /> Overall Details</div>
                  <div className="dvsa-card-body">
                    <div className="dvsa-stat"><span className="dvsa-stat-label">Scanned Papers</span><span className="dvsa-stat-value dvsa-v-total">{totalPapers}</span></div>
                    <div className="dvsa-stat"><span className="dvsa-stat-label">Assigned Scripts</span><span className="dvsa-stat-value dvsa-v-assigned">{assignedPapers}</span></div>
                    <div className="dvsa-stat"><span className="dvsa-stat-label">Unassigned Scripts</span><span className="dvsa-stat-value dvsa-v-unassigned">{unassigned}</span></div>
                  </div>
                </div>
                <div className="dvsa-card">
                  <div className="dvsa-card-head"><FolderCog size={16} /> Current Selection</div>
                  <div className="dvsa-card-body">
                    <div className="dvsa-stat"><span className="dvsa-stat-label">Ready to be assigned</span><span className="dvsa-stat-value dvsa-v-ready">{readyToAssign}</span></div>
                    <div className="dvsa-stat"><span className="dvsa-stat-label">Yet to be assigned</span><span className="dvsa-stat-value dvsa-v-yet">{yetToAssign}</span></div>
                  </div>
                </div>
              </div>
              <div className="col-md-9">
                <div className="dvsa-card" style={{ marginBottom: 0 }}>
                  <div className="dvsa-card-head"><Upload size={16} /> Assign to Evaluators</div>
                  {detail.details.length > 0 && unassigned <= 0 ? (
                    <div className="dvsa-banner dvsa-banner-success" style={{ margin: "12px 16px 0" }}>
                      <CheckCircle2 size={16} style={{ marginRight: 8, verticalAlign: "-3px" }} />
                      All {totalPapers} scanned paper(s) are already assigned. Use the <strong>Reclaim</strong> action to free papers before re-assigning.
                    </div>
                  ) : null}
                  <div className="dvsa-card-body" style={{ padding: 0 }}>
                    <div className="dvsa-table-wrap" style={{ maxHeight: "calc(100vh - 280px)" }}>
                      <table className="dvsa-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>User ID</th>
                            <th>Name</th>
                            <th>Contact</th>
                            <th>Email</th>
                            <th>School</th>
                            <th>Assigned</th>
                            <th>Pending</th>
                            <th>In&nbsp;Progress</th>
                            <th>Assign</th>
                            <th>Reclaim</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.details.length === 0 ? (
                            <tr><td colSpan={11}><div className="dvsa-empty"><div className="dvsa-empty-icon"><Inbox size={26} /></div>No instructor found for the corresponding course.</div></td></tr>
                          ) : (
                            detail.details.map((row, i) => (
                              <tr key={row.instructorId}>
                                <td>{i + 1}</td>
                                <td style={{ fontWeight: 600 }}>{row.instructorName}</td>
                                <td>{row.userName}</td>
                                <td>{row.contactNumber}</td>
                                <td className="dvsa-trunc" title={row.emailAddress}>{row.emailAddress}</td>
                                <td className="dvsa-trunc" title={row.schoolName}>{row.schoolName}</td>
                                <td><span className="dvsa-pill dvsa-pill-success">{row.allocated}</span></td>
                                <td><span className="dvsa-pill dvsa-pill-info">{row.pending}</span></td>
                                <td><span className="dvsa-pill dvsa-pill-warn">{row.inProgress}</span></td>
                                <td>
                                  <input
                                    type="number"
                                    min={0}
                                    className="dvsa-num-input"
                                    max={unassigned}
                                    disabled={unassigned <= 0}
                                    value={assignInputs[row.instructorId] ?? 0}
                                    onChange={(e: any) => setAssignInput(row.instructorId, e.target.value)}
                                  />
                                </td>
                                <td>
                                  {row.pending > 0 || row.inProgress > 0 ? (
                                    <span className="dvsa-icon-btn dvsa-icon-reclaim" title="Reclaim papers" onClick={() => openReclaim(row)}>
                                      <MinusCircle size={18} />
                                    </span>
                                  ) : (
                                    <span className="dvsa-icon-btn dvsa-icon-reclaim disabled"><MinusCircle size={18} /></span>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="dvsa-btn dvsa-btn-ghost" onClick={() => setShowManage(false)}>Close</button>
          <button className="dvsa-btn dvsa-btn-primary" onClick={submitAssign} disabled={assigning || !detail || readyToAssign <= 0}>
            {assigning ? <><Spinner animation="border" size="sm" className="me-1" />Assigning…</> : `Assign Papers${readyToAssign > 0 ? ` (${readyToAssign})` : ""}`}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ============ Reclaim Modal ============ */}
      <Modal className="dvsa-modal" show={!!reclaimRow} onHide={() => { setReclaimRow(null); setReclaimConfirm(0); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reclaim Scanned Papers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reclaimRow ? (
            <>
              <div className="dvsa-card" style={{ marginBottom: 16 }}>
                <div className="dvsa-card-body" style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <div className="dvsa-section-title" style={{ marginBottom: 2 }}>Instructor</div>
                    <div style={{ fontWeight: 700, color: "var(--text-strong)" }}>{reclaimRow.instructorName}</div>
                  </div>
                  <span className="dvsa-pill dvsa-pill-info" title="Pending">{reclaimRow.pending} pending</span>
                  <span className="dvsa-pill dvsa-pill-warn" title="In progress">{reclaimRow.inProgress} in&nbsp;progress</span>
                </div>
              </div>
              <div className="dvsa-section-title">Number of papers to reclaim</div>
              <input
                type="number"
                min={0}
                className="dvsa-num-input"
                style={{ width: "100%" }}
                value={reclaimCount}
                onChange={(e: any) => { setReclaimCount(Number(e.target.value)); setReclaimConfirm(0); }}
              />
              {reclaimConfirm > 0 ? (
                <div className="dvsa-banner dvsa-banner-danger" style={{ marginTop: 14, marginBottom: 0 }}>
                  <AlertTriangle size={16} style={{ marginRight: 6, verticalAlign: "-3px" }} />
                  <strong>{reclaimConfirm} paper(s) are currently In&nbsp;Progress.</strong> Once reclaimed, the
                  evaluation progress will be lost — this action is irreversible.
                </div>
              ) : null}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button className="dvsa-btn dvsa-btn-ghost" onClick={() => { setReclaimRow(null); setReclaimConfirm(0); }}>Cancel</button>
          {reclaimConfirm > 0 ? (
            <button className="dvsa-btn dvsa-btn-danger" onClick={() => doReclaim(true)} disabled={reclaiming}>
              {reclaiming ? "Reclaiming…" : "Yes, Reclaim"}
            </button>
          ) : (
            <button className="dvsa-btn dvsa-btn-primary" onClick={() => doReclaim(false)} disabled={reclaiming}>
              {reclaiming ? "Reclaiming…" : "Reclaim"}
            </button>
          )}
        </Modal.Footer>
      </Modal>

      {/* ============ Uploaded Scanned Paper Modal ============ */}
      <Modal className="dvsa-modal" dialogClassName="dvs-wide-dialog" show={showUploaded} onHide={() => setShowUploaded(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Uploaded Scanned Papers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap mb-3" style={{ gap: 10 }}>
            <button className="dvsa-action-btn dvsa-btn-upload" onClick={() => openAlert("notSubmitted")}>
              <MessageSquare size={16} />Send Alert · Not Submitted
            </button>
            <button className="dvsa-action-btn" style={{ background: "linear-gradient(135deg,var(--brand-accent),var(--brand-accent))" }} onClick={() => openAlert("notEvaluated")}>
              <Mail size={16} />Send Alert · Not Evaluated
            </button>
          </div>

          <div className="dvsa-card" style={{ marginBottom: 0 }}>
            <div className="dvsa-table-wrap" style={{ maxHeight: "calc(100vh - 320px)" }}>
              {uploadedLoading ? (
                <div className="dvsa-empty"><Spinner animation="border" /></div>
              ) : (
                <table className="dvsa-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Student Name</th>
                      <th>Student Code</th>
                      <th>Reported</th>
                      <th>Paper</th>
                      <th>Evaluator</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedPapers.length === 0 ? (
                      <tr><td colSpan={7}><div className="dvsa-empty"><div className="dvsa-empty-icon"><Inbox size={26} /></div>No uploaded papers found.</div></td></tr>
                    ) : (
                      uploadedPapers.map((p, i) => (
                        <tr key={p.id}>
                          <td>{i + 1}</td>
                          <td style={{ fontWeight: 600 }}>{p.studentName}</td>
                          <td>{p.studentCode}</td>
                          <td>
                            {p.issueReported ? (
                              <span className="dvsa-icon-btn dvsa-icon-warn" title={p.issueReportedRemarks || "Issue reported"}>
                                <AlertTriangle size={16} />
                              </span>
                            ) : null}
                          </td>
                          <td>
                            {p.url ? (
                              <a className="dvsa-icon-btn dvsa-icon-download" href={p.url} target="_blank" rel="noreferrer" title="Download / View">
                                <Download size={16} />
                              </a>
                            ) : (
                              <span style={{ color: "var(--text-faint)" }}>—</span>
                            )}
                          </td>
                          <td>{p.evaluatorName || <span style={{ color: "var(--text-faint)" }}>—</span>}</td>
                          <td>
                            <span className={`dvsa-pill ${p.evaluationStatus === "Evaluated" ? "dvsa-pill-success" : "dvsa-pill-info"}`}>
                              {p.evaluationStatus || "—"}
                            </span>
                            {p.evaluationStatus === "Evaluated" ? (
                              <span className="dvsa-icon-btn dvsa-icon-reopen" style={{ marginLeft: 8 }} title="Re-open evaluation" onClick={() => setReopenPaper(p)}>
                                <RotateCcw size={16} />
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="dvsa-btn dvsa-btn-ghost" onClick={() => setShowUploaded(false)}>Close</button>
        </Modal.Footer>
      </Modal>

      {/* ============ Send Alert options Modal ============ */}
      <Modal className="dvsa-modal" show={!!alertType} onHide={() => setAlertType(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{alertType === "notEvaluated" ? "Alert Evaluators" : "Alert Students"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="dvsa-section-title">Choose how to notify</div>
          <div className="dvsa-channels">
            <div className={`dvsa-channel ${alertChannels.sms ? "active" : ""}`} onClick={() => toggleChannel("sms")}>
              <span className="dvsa-channel-ico" style={{ background: "var(--status-info)" }}><MessageSquare size={18} /></span>
              <span className="dvsa-channel-label">SMS</span>
              {alertChannels.sms ? <CheckCircle2 size={20} color="var(--brand-primary)" /> : <span style={{ width: 20 }} />}
            </div>
            <div className={`dvsa-channel ${alertChannels.email ? "active" : ""}`} onClick={() => toggleChannel("email")}>
              <span className="dvsa-channel-ico" style={{ background: "var(--brand-primary)" }}><Mail size={18} /></span>
              <span className="dvsa-channel-label">Email</span>
              {alertChannels.email ? <CheckCircle2 size={20} color="var(--brand-primary)" /> : <span style={{ width: 20 }} />}
            </div>
            <div className={`dvsa-channel ${alertChannels.whatsApp ? "active" : ""}`} onClick={() => toggleChannel("whatsApp")}>
              <span className="dvsa-channel-ico" style={{ background: "var(--status-success)" }}><MessageCircle size={18} /></span>
              <span className="dvsa-channel-label">WhatsApp</span>
              {alertChannels.whatsApp ? <CheckCircle2 size={20} color="var(--brand-primary)" /> : <span style={{ width: 20 }} />}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="dvsa-btn dvsa-btn-ghost" onClick={() => setAlertType(null)}>Cancel</button>
          <button className="dvsa-btn dvsa-btn-primary" onClick={submitAlert} disabled={alertSending}>
            {alertSending ? <><Spinner animation="border" size="sm" className="me-1" />Sending…</> : "Send Alert"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ============ Re-open confirm Modal ============ */}
      <Modal className="dvsa-modal" show={!!reopenPaper} onHide={() => setReopenPaper(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Re-open Evaluation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reopenPaper ? (
            <>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span className="dvsa-icon-btn dvsa-icon-warn" style={{ width: 40, height: 40, flexShrink: 0 }}><AlertTriangle size={20} /></span>
                <div>
                  <div style={{ color: "var(--text-strong)" }}>
                    Re-open the evaluation for <strong>{reopenPaper.studentName} [{reopenPaper.studentCode}]</strong>?
                  </div>
                  <div style={{ color: "var(--status-danger)", marginTop: 8, fontSize: "0.85rem", fontWeight: 600 }}>
                    Note: This process is irreversible. Do you still want to continue?
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button className="dvsa-btn dvsa-btn-ghost" onClick={() => setReopenPaper(null)}>Close</button>
          <button className="dvsa-btn dvsa-btn-danger" onClick={submitReopen} disabled={reopening}>
            {reopening ? "Processing…" : "Confirm"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DvsPaperAllocationPanel;
